import { useCallback, useState } from 'react';
import { FormField, Header, HStack, type HVStackProps, TextInput, VStack } from '@direct-frontend/components';
import { type BaseValidationError, useField, useFormInstance, useFormState } from '@adv-frontend/form-kit';
import { getGeoSuggest } from '@/entities/geoSuggest';
import { GeoInput } from '@/entities/geoSuggest';
import { FormFieldLabel } from '@/shared/components/FormFieldLabel';
import { type CourierAddressPickerValue } from '../../lib/types/CourierAddressPickerValue';
import { courierAddressForm } from '../../model/courierAddressForm';
import { CourierSubmitButton } from '../CourierSubmitButton';
import styles from './CourierAddressForm.module.css';

const ERRORS_MAPPING: Record<string, string> = {
    UNSUPPORTED_ADDRESS: 'Не можем доставить по этому адресу',
    REQUIRED_DELIVERY_FLOOR: 'Необходимо указать этаж',
};

const buildErrorMessage = (errors: BaseValidationError[]): string | undefined => {
    const [validationError] = errors;

    return validationError?.code ? ERRORS_MAPPING[validationError.code] : undefined;
};

interface CourierAddressFormProps extends HVStackProps {
    formId: string;
    onSubmit?: (values: CourierAddressPickerValue) => void;
}

export const CourierAddressForm = (props: CourierAddressFormProps) => {
    const { formId, onSubmit: propOnSubmit, ...other } = props;
    const formInstance = useFormInstance({ id: formId, form: courierAddressForm });
    const { fields, revalidate } = formInstance;
    const { isValid, isSubmitting } = useFormState({ id: formId, form: courierAddressForm });

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const fieldFullAddress = useField(fields.fullAddress);
    const fieldFloor = useField(fields.floor);
    const fieldAppartment = useField(fields.appartment);
    const fieldEntrance = useField(fields.entrance);
    const fieldIntercom = useField(fields.intercom);
    const fieldComment = useField(fields.comment);

    const onLoadNearestHouse = useCallback(
        async (text: string) => {
            setIsLoading(true);

            try {
                const [address = ''] = await getGeoSuggest({ text, types: 'house', results: 1 });

                // Установка значения после асинхронной логики должна быть описана в асинхронном экшене: DIRECT-302840
                // eslint-disable-next-line @direct-frontend/modules/form-kit-require-async-action -- см. выше
                fields.fullAddress.setValue(address);
                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
            }
        },
        [fields]
    );

    const onSubmit = useCallback(
        async (callback?: VoidFunction) => {
            revalidate();

            callback?.();

            const values = await formInstance.submit();

            if (values) {
                propOnSubmit?.(values);
            }
        },
        [formInstance, revalidate, propOnSubmit]
    );

    return (
        <VStack justifyContent="space-between" gap="16" {...other}>
            <VStack gap="16">
                <Header level="h2" className={styles.title}>
                    Укажите адрес доставки
                </Header>
                <VStack gap="12">
                    <FormField
                        size="s"
                        className={styles.field}
                        label={<FormFieldLabel isRequired>Адрес</FormFieldLabel>}
                        errorMessage={buildErrorMessage(fieldFullAddress.state.errors)}
                    >
                        {(inputProps) => (
                            <GeoInput
                                {...inputProps}
                                value={fieldFullAddress.value}
                                onChange={onLoadNearestHouse}
                                isLoading={isLoading}
                                types="house"
                                size="m"
                            />
                        )}
                    </FormField>
                    <HStack gap="16">
                        <FormField className={styles.field} label={<FormFieldLabel>Квартира</FormFieldLabel>}>
                            {(inputProps) => (
                                <TextInput
                                    {...inputProps}
                                    {...fieldAppartment.getProps()}
                                    isLoading={isLoading}
                                    color="contour"
                                    type="number"
                                    size="m"
                                />
                            )}
                        </FormField>
                        <FormField
                            className={styles.field}
                            label={<FormFieldLabel>Этаж</FormFieldLabel>}
                            errorMessage={buildErrorMessage(fieldFloor.state.errors)}
                        >
                            {(inputProps) => (
                                <TextInput
                                    {...inputProps}
                                    {...fieldFloor.getProps()}
                                    isLoading={isLoading}
                                    color="contour"
                                    type="number"
                                    size="m"
                                />
                            )}
                        </FormField>
                    </HStack>
                    <HStack gap="16">
                        <FormField className={styles.field} label={<FormFieldLabel>Подъезд</FormFieldLabel>}>
                            {(inputProps) => (
                                <TextInput
                                    {...inputProps}
                                    {...fieldEntrance.getProps()}
                                    isLoading={isLoading}
                                    color="contour"
                                    type="number"
                                    size="m"
                                />
                            )}
                        </FormField>
                        <FormField className={styles.field} label={<FormFieldLabel>Домофон</FormFieldLabel>}>
                            {(inputProps) => (
                                <TextInput
                                    {...inputProps}
                                    {...fieldIntercom.getProps()}
                                    isLoading={isLoading}
                                    color="contour"
                                    size="m"
                                />
                            )}
                        </FormField>
                    </HStack>
                    <FormField
                        className={styles.field}
                        label={<FormFieldLabel>Комментарий для курьера</FormFieldLabel>}
                    >
                        {(inputProps) => (
                            <TextInput
                                {...inputProps}
                                {...fieldComment.getProps()}
                                isLoading={isLoading}
                                color="contour"
                                size="m"
                            />
                        )}
                    </FormField>
                </VStack>
            </VStack>

            <CourierSubmitButton isSubmitting={!isValid || isSubmitting || isLoading} onSubmit={onSubmit}>
                Привезти сюда
            </CourierSubmitButton>
        </VStack>
    );
};
